import React, { useState, useEffect } from 'react'
import styles from './ConversationalMode.module.scss'
import MessageBubble from './MessageBubble.tsx'
import WorkOffersList from './WorkOffersList.tsx'
import ProfessionalsList from './ProfessionalsList.tsx'
import MaterialQuotationsList from './MaterialQuotationsList.tsx'
import WorksToQuoteList from './WorksToQuoteList.tsx'
import ServicesAndProductsList from './ServicesAndProductsList.tsx'
import MessageLimitCard from './MessageLimitCard.tsx'
import { Message } from '../utils/conversationStorage.ts'
import { Loader2 } from 'lucide-react'
import type { DesktopSuggestion } from '../dashboardActionsConfig.tsx'
import { useSelector } from 'react-redux'

interface MessageListProps {
  messages: Message[]
  isLoading?: boolean
  matchedSuggestion?: DesktopSuggestion | null
  onSuggestionAction?: () => void
  allSuggestions?: DesktopSuggestion[]
  hasReachedLimit?: boolean
  remainingMessages?: number
  limit?: number
  userData?: any
}

const MessageList: React.FC<MessageListProps> = ({ 
  messages, 
  isLoading = false,
  onSuggestionAction,
  allSuggestions = [],
  hasReachedLimit = false,
  remainingMessages = 0,
  limit = 0,
  userData
}) => {
  const isUserPremium = useSelector((state: any) => state.general?.isUserPremium ?? false)
  const [typingStates, setTypingStates] = useState<Record<string, boolean>>({})
  
  // Limpiar estados de typing de mensajes que ya no existen
  useEffect(() => {
    const messageIds = new Set(messages.map(m => m.id))
    setTypingStates(prev => {
      const cleaned: Record<string, boolean> = {}
      Object.keys(prev).forEach(id => {
        if (messageIds.has(id)) {
          cleaned[id] = prev[id]
        }
      })
      return cleaned
    })
  }, [messages])

  // Memoizar el callback para evitar recrearlo en cada render
  const handleTypingStateChange = React.useCallback((messageId: string, isTyping: boolean) => {
    setTypingStates(prev => {
      // Solo actualizar si el valor realmente cambió
      if (prev[messageId] === isTyping) {
        return prev
      }
      return {
        ...prev,
        [messageId]: isTyping
      }
    })
  }, [])

  if (messages.length === 0 && !isLoading) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateText}>
          Hacé tu primera pregunta y te ayudo a encontrar lo que necesitás.
        </p>
      </div>
    )
  }

  const lastMessage = messages[messages.length - 1]

  const getSuggestionForMessage = (message: Message): DesktopSuggestion | null => {
    if (!message.actionId) return null
    return allSuggestions.find(s => s.id === message.actionId) || null
  }

  const renderDataCards = (message: Message) => {
    if (!message.needsData || !message.data) return null

    const { actionId, data, dataTotal = 0, quotationType, isPublic, noResultsMessage } = message

    if (actionId === 'postularme-ofertas' && data.length > 0) {
      return (
        <WorkOffersList 
          offers={data}
          total={dataTotal}
        />
      )
    }

    if (actionId === 'encontrar-profesionales') {
      return (
        <ProfessionalsList 
          professionals={data}
          total={dataTotal}
          noResultsMessage={noResultsMessage}
        />
      )
    }

    if (actionId === 'cotizaciones-materiales') {
      return (
        <MaterialQuotationsList 
          quotations={data}
          total={dataTotal}
          quotationType={quotationType || 'privadas'}
          isPublic={isPublic || false}
          noResultsMessage={noResultsMessage}
        />
      )
    }

    if (actionId === 'cotizar-obras') {
      return (
        <WorksToQuoteList
          works={data}
          total={dataTotal}
          workVisibility={(message as any).workVisibility || 'privadas'}
          isPublic={isPublic || false}
          noResultsMessage={noResultsMessage}
        />
      )
    }

    if (actionId === 'explorar-servicios') {
      return (
        <ServicesAndProductsList
          items={data}
          total={dataTotal}
          noResultsMessage={noResultsMessage}
        />
      )
    }

    return null
  }

  const isGuideMessage = (content: string): boolean => {
    return content.includes('Paso 1:') || content.includes('Paso 1 :')
  }

  return (
    <div className={styles.messageList}>
      {messages.map((message) => {
        const suggestion = getSuggestionForMessage(message)
        const isLastMessage = message.id === lastMessage?.id
        const isGuide = message.role === 'assistant' && isGuideMessage(message.content)
        
        const shouldShowAction = 
          message.role === 'assistant' && 
          suggestion && 
          message.actionId === suggestion.id && 
          (isLastMessage || isGuide) &&
          !isLoading &&
          onSuggestionAction
        
        const handleActionClick = shouldShowAction && suggestion ? () => {
          if (suggestion && suggestion.action) {
            suggestion.action()
          } else if (onSuggestionAction) {
            onSuggestionAction()
          }
        } : undefined
        
        const isMessageTyping = typingStates[message.id] ?? false
        const shouldShowDataCards = message.role === 'assistant' && !isMessageTyping && renderDataCards(message)
        
        return (
          <React.Fragment key={message.id}>
            <MessageBubble 
              message={message}
              suggestion={shouldShowAction ? suggestion : null}
              onSuggestionAction={handleActionClick}
              isLatest={isLastMessage && !isLoading}
              onTypingStateChange={handleTypingStateChange}
            />
            {shouldShowDataCards}
          </React.Fragment>
        )
      })}
      {hasReachedLimit && !isUserPremium && messages.length > 0 && (
        <MessageLimitCard
          userData={userData}
          isUserPremium={isUserPremium}
          remainingMessages={remainingMessages}
          limit={limit}
        />
      )}
      {isLoading && (
        <div className={styles.loadingIndicator}>
          <Loader2 size={20} className={styles.loadingSpinner} />
          <span>Pensando...</span>
        </div>
      )}
    </div>
  )
}

export default MessageList
